/**
 * Date Filtering Utilities
 *
 * Pure functions for filtering dates and bookings by date criteria.
 * Used for weekend filtering, date range filtering, and date comparisons.
 */

import type { IBooking } from "@/services/types";
import { isSameDay, isWeekend, isWithinInterval, startOfDay } from "date-fns";

/**
 * Filter array of dates to only weekdays (Monday-Friday)
 * @param dates Array of dates to filter
 * @returns Array of dates excluding weekends
 */
export function filterWeekdays(dates: Date[]): Date[] {
  return dates.filter((date) => !isWeekend(date));
}

/**
 * Filter array of dates to only weekends (Saturday-Sunday)
 * @param dates Array of dates to filter
 * @returns Array of weekend dates only
 */
export function filterWeekends(dates: Date[]): Date[] {
  return dates.filter((date) => isWeekend(date));
}

/**
 * Get visible dates based on hideWeekend setting
 * Used throughout the application to handle weekend visibility
 * @param dates Array of dates
 * @param hideWeekend Whether to hide weekends
 * @returns Filtered array of dates
 */
export function getVisibleDates(dates: Date[], hideWeekend: boolean): Date[] {
  if (!hideWeekend) {
    return dates;
  }
  return filterWeekdays(dates);
}

/**
 * Filter bookings to those on a specific date
 * Compares dates ignoring time component
 * @param bookings Array of bookings
 * @param date Target date
 * @returns Bookings matching the specified date
 */
export function filterBookingsByDate(
  bookings: IBooking[],
  date: Date,
): IBooking[] {
  return bookings.filter((booking) => isSameDay(booking.date, date));
}

/**
 * Filter bookings to those within a date range (inclusive)
 * @param bookings Array of bookings
 * @param startDate Range start (inclusive)
 * @param endDate Range end (inclusive)
 * @returns Bookings within the specified date range
 */
export function filterBookingsByDateRange(
  bookings: IBooking[],
  startDate: Date,
  endDate: Date,
): IBooking[] {
  // Normalize dates to start of day to ignore time component
  const start = startOfDay(startDate);
  const end = startOfDay(endDate);

  // Handle reversed dates (swap if necessary)
  const [rangeStart, rangeEnd] = start <= end ? [start, end] : [end, start];

  return bookings.filter((booking) => {
    const bookingDate = startOfDay(booking.date);
    return isWithinInterval(bookingDate, { start: rangeStart, end: rangeEnd });
  });
}
